import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, getToken, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, ArrowLeft, FileDown, Calculator, Map as MapIcon } from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import AddressAutocomplete from "@/components/public/AddressAutocomplete";

const pickIcon = L.divIcon({
  html: '<div style="background:#0A0A0A;color:#fff;border:2px solid #fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:11px;box-shadow:0 1px 4px rgba(0,0,0,.4)">A</div>',
  className: "", iconSize: [28,28], iconAnchor: [14,14],
});
const dropIcon = L.divIcon({
  html: '<div style="background:#E63946;color:#fff;border:2px solid #fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:11px;box-shadow:0 1px 4px rgba(0,0,0,.4)">B</div>',
  className: "", iconSize: [28,28], iconAnchor: [14,14],
});

export default function NewServicePage() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState({
    booking_number: "",
    client_name: "",
    client_phone: "",
    client_email: "",
    pickup_date: new Date().toISOString().slice(0, 10),
    pickup_time: "",
    pickup_location: "",
    dropoff_location: "",
    passengers: 1,
    price: "",
    flight_number: "",
    vehicle_type: "",
    notes: "",
    driver_id: "none",
    provider: "ds_travel_tours",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [created, setCreated] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [estLoading, setEstLoading] = useState(false);
  const [estErr, setEstErr] = useState("");

  const estimatePrice = async () => {
    setEstErr("");
    if (!form.pickup_location.trim() || !form.dropoff_location.trim()) {
      setEstErr("Introduce origen y destino antes de calcular.");
      return;
    }
    setEstLoading(true);
    try {
      const { data } = await api.post("/services/estimate-price", {
        pickup_location: form.pickup_location.trim(),
        dropoff_location: form.dropoff_location.trim(),
        vehicle_type: form.vehicle_type || "standard",
      });
      setEstimate(data);
    } catch (e2) {
      setEstErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
      setEstimate(null);
    } finally {
      setEstLoading(false);
    }
  };

  const applySuggestedPrice = () => {
    if (estimate?.suggested_price != null) {
      setForm((p) => ({ ...p, price: String(estimate.suggested_price) }));
      toast.success("Precio sugerido aplicado");
    }
  };

  useEffect(() => {
    api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {});
  }, []);

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const body = {
        ...form,
        passengers: parseInt(form.passengers) || 1,
        price: form.price ? parseFloat(form.price) : null,
        driver_id: form.driver_id === "none" ? null : form.driver_id,
      };
      const { data } = await api.post("/services", body);
      toast.success("Reserva creada");
      setCreated(data);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPDF = () => {
    if (!created) return;
    window.open(
      `${API}/services/${created.id}/export.pdf?auth=${encodeURIComponent(getToken())}`,
      "_blank"
    );
  };

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-5xl mx-auto" data-testid="new-service-page">
      <button
        onClick={() => navigate(-1)}
        className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black flex items-center gap-2 mb-6"
      >
        <ArrowLeft size={14} /> Volver
      </button>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
          Centro de Control
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
          Nueva reserva
        </h1>
      </div>

      {created ? (
        <div className="border-2 border-black bg-white p-8 animate-fade-up" data-testid="service-created-card">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
            Reserva creada
          </div>
          <div className="font-display text-3xl font-bold font-mono mb-4">
            {created.booking_number}
          </div>
          <p className="text-sm text-zinc-600 mb-6">
            La reserva se creó correctamente. Puedes descargar el PDF de confirmación o ir al detalle.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={downloadPDF}
              className="rounded-none btn-primary h-11 px-5 text-xs uppercase tracking-widest"
              data-testid="download-confirmation-pdf"
            >
              <FileDown size={14} className="mr-2" /> Descargar confirmación PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/services/${created.id}`)}
              className="rounded-none border-black h-11 px-5 text-xs uppercase tracking-widest"
            >
              Ver detalle
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setCreated(null);
                setForm({
                  booking_number: "",
                  client_name: "",
                  client_phone: "",
                  client_email: "",
                  pickup_date: new Date().toISOString().slice(0, 10),
                  pickup_time: "",
                  pickup_location: "",
                  dropoff_location: "",
                  passengers: 1,
                  price: "",
                  flight_number: "",
                  vehicle_type: "",
                  notes: "",
                  driver_id: "none",
                  provider: "ds_travel_tours",
                });
              }}
              className="rounded-none border-black h-11 px-5 text-xs uppercase tracking-widest"
            >
              Crear otra
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="border border-black bg-white" data-testid="new-service-form">
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5 border-b border-black">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest">Proveedor de la reserva *</Label>
              <Select
                value={form.provider}
                onValueChange={(v) => setForm((p) => ({ ...p, provider: v }))}
              >
                <SelectTrigger
                  className="mt-2 h-11 rounded-none border-black"
                  data-testid="provider-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="ds_travel_tours">DS Travel Tours (cliente directo)</SelectItem>
                  <SelectItem value="booking">Booking.com</SelectItem>
                  <SelectItem value="jayride">Jayride</SelectItem>
                  <SelectItem value="viator">Viator</SelectItem>
                </SelectContent>
              </Select>
              {form.provider !== "ds_travel_tours" && (
                <div className="mt-2 text-xs text-zinc-600 bg-[#F5B301]/20 border border-black px-3 py-2">
                  Esta reserva la paga <b>{form.provider === "booking" ? "Booking.com" : form.provider === "jayride" ? "Jayride" : "Viator"}</b>.
                  El cliente no verá el bloque de pago en su consulta.
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">
                {form.provider === "ds_travel_tours"
                  ? "Número de reserva"
                  : "Nº de reserva del proveedor *"}
              </Label>
              <Input
                value={form.booking_number}
                onChange={onChange("booking_number")}
                required={form.provider !== "ds_travel_tours"}
                className="mt-2 h-11 rounded-none border-black"
                placeholder={
                  form.provider === "ds_travel_tours"
                    ? "Se asignará automáticamente (DSG-XXXXX)"
                    : form.provider === "booking"
                    ? "Ej. 1234567890"
                    : form.provider === "jayride"
                    ? "Ej. JR-ABC123"
                    : form.provider === "viator"
                    ? "Ej. BR-123456789"
                    : "RSV-0001"
                }
                data-testid="booking-number-input"
              />
              {form.provider === "ds_travel_tours" && !form.booking_number && (
                <div className="mt-1 text-[11px] text-zinc-500">
                  Déjalo vacío y el sistema generará un número DSG-XXXXX automáticamente.
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Nº vuelo / Observaciones</Label>
              <Input
                value={form.flight_number}
                onChange={onChange("flight_number")}
                className="mt-2 h-11 rounded-none border-black"
                placeholder="IB3201"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Cliente *</Label>
              <Input
                value={form.client_name}
                onChange={onChange("client_name")}
                required
                className="mt-2 h-11 rounded-none border-black"
                data-testid="client-name-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Teléfono cliente *</Label>
              <Input
                value={form.client_phone}
                onChange={onChange("client_phone")}
                required
                className="mt-2 h-11 rounded-none border-black"
                placeholder="+34 611 22 33 44"
                data-testid="client-phone-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Email del cliente</Label>
              <Input
                type="email"
                value={form.client_email}
                onChange={onChange("client_email")}
                className="mt-2 h-11 rounded-none border-black"
                placeholder="cliente@email.com"
                data-testid="client-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Vehículo solicitado</Label>
              <Select
                value={form.vehicle_type}
                onValueChange={(v) => setForm((p) => ({ ...p, vehicle_type: v }))}
              >
                <SelectTrigger
                  className="mt-2 h-11 rounded-none border-black"
                  data-testid="vehicle-type-select"
                >
                  <SelectValue placeholder="Selecciona vehículo" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="mini vans">Mini Vans</SelectItem>
                  <SelectItem value="minibus">Minibus</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Fecha de recogida *</Label>
              <Input
                type="date"
                value={form.pickup_date}
                onChange={onChange("pickup_date")}
                required
                className="mt-2 h-11 rounded-none border-black"
                data-testid="pickup-date-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Hora *</Label>
              <Input
                type="time"
                value={form.pickup_time}
                onChange={onChange("pickup_time")}
                required
                className="mt-2 h-11 rounded-none border-black"
                data-testid="pickup-time-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Origen *</Label>
              <div className="mt-2">
                <AddressAutocomplete
                  value={form.pickup_location}
                  onChange={(v) => setForm((p) => ({ ...p, pickup_location: v }))}
                  placeholder="Hotel, dirección…"
                  testId="pickup-location-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Destino *</Label>
              <div className="mt-2">
                <AddressAutocomplete
                  value={form.dropoff_location}
                  onChange={(v) => setForm((p) => ({ ...p, dropoff_location: v }))}
                  placeholder="Hotel, dirección…"
                  testId="dropoff-location-input"
                />
              </div>
            </div>

            {/* Price calculator block */}
            <div className="md:col-span-2 border-2 border-black p-4 bg-zinc-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <div>
                  <div className="font-display text-sm uppercase tracking-widest font-bold flex items-center gap-2">
                    <Calculator size={16} /> Calculadora de precio
                  </div>
                  <div className="text-xs text-zinc-600 mt-1">
                    Calcula los km y el precio sugerido según el vehículo. Solo cobra por km si <b>sale</b> de la Comunidad de Madrid.
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={estimatePrice}
                  disabled={estLoading}
                  variant="outline"
                  className="rounded-none border-black h-10 px-4 text-xs uppercase tracking-widest"
                  data-testid="estimate-price-btn"
                >
                  {estLoading ? <Loader2 className="animate-spin" size={14} /> : (
                    <><MapIcon size={14} className="mr-2" /> Calcular</>
                  )}
                </Button>
              </div>
              {estErr && (
                <div className="text-sm text-[#E63946] bg-red-50 border border-[#E63946] px-3 py-2 mb-3">
                  {estErr}
                </div>
              )}
              {estimate && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="estimate-result">
                  <div className="border border-black bg-white p-3">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Distancia</div>
                    <div className="font-display text-xl font-bold">{estimate.distance_km} km</div>
                  </div>
                  <div className="border border-black bg-white p-3">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Vehículo</div>
                    <div className="font-semibold capitalize">{estimate.vehicle_type}</div>
                    <div className="text-[10px] text-zinc-500 mt-1">
                      Base {estimate.base_price}€{estimate.leaves_madrid ? ` · +${estimate.price_per_km}€/km` : ""}
                    </div>
                  </div>
                  <div className="border-2 border-black bg-black text-white p-3">
                    <div className="text-[10px] uppercase tracking-widest opacity-70">
                      {estimate.leaves_madrid ? "Sale CAM" : "Dentro CAM"} · Sugerido
                    </div>
                    <div className="font-display text-2xl font-bold">{estimate.suggested_price.toFixed(2)}€</div>
                    <button
                      type="button"
                      onClick={applySuggestedPrice}
                      className="mt-2 text-[10px] uppercase tracking-widest underline"
                      data-testid="apply-price-btn"
                    >
                      Usar este precio →
                    </button>
                  </div>
                  {estimate.geometry?.coordinates && (
                    <div className="sm:col-span-3 border border-black bg-white" style={{ height: 260 }}>
                      <MapContainer
                        bounds={estimate.geometry.coordinates.map((c) => [c[1], c[0]])}
                        style={{ height: "100%", width: "100%" }}
                        scrollWheelZoom={false}
                      >
                        <TileLayer
                          attribution='&copy; OpenStreetMap'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker position={[estimate.pickup.lat, estimate.pickup.lng]} icon={pickIcon} />
                        <Marker position={[estimate.dropoff.lat, estimate.dropoff.lng]} icon={dropIcon} />
                        <Polyline
                          positions={estimate.geometry.coordinates.map((c) => [c[1], c[0]])}
                          pathOptions={{ color: "#0A0A0A", weight: 4 }}
                        />
                      </MapContainer>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest">Pasajeros</Label>
              <Input
                type="number"
                min={1}
                value={form.passengers}
                onChange={onChange("passengers")}
                className="mt-2 h-11 rounded-none border-black"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Precio (€)</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={form.price}
                onChange={onChange("price")}
                className="mt-2 h-11 rounded-none border-black"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest">Asignar conductor</Label>
              <Select
                value={form.driver_id}
                onValueChange={(v) => setForm((p) => ({ ...p, driver_id: v }))}
              >
                <SelectTrigger
                  className="mt-2 h-11 rounded-none border-black"
                  data-testid="assign-driver-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {drivers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} {d.phone ? `· ${d.phone}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest">Notas</Label>
              <Textarea
                value={form.notes}
                onChange={onChange("notes")}
                className="mt-2 rounded-none border-black min-h-[100px]"
                placeholder="Instrucciones especiales, equipaje, etc."
              />
            </div>
          </div>

          {err && (
            <div className="mx-6 mt-4 text-sm text-red-700 bg-red-50 border border-red-300 px-3 py-2">
              {err}
            </div>
          )}

          <div className="p-4 flex justify-end gap-3 bg-zinc-50 border-t border-black">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="rounded-none border-black h-11 px-5 text-xs uppercase tracking-widest"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-none btn-primary h-11 px-6 text-xs uppercase tracking-widest"
              data-testid="submit-service-btn"
            >
              {submitting ? <Loader2 className="animate-spin" size={16} /> : "Crear reserva"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
